import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/pages/home/components/MovieCard';
import { movies } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  // Related movies - same category, excluding current
  const relatedMovies = movie
    ? movies.filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id).slice(0, 4)
    : [];

  // Fallback: popular movies if not enough related
  const recommendedMovies = relatedMovies.length >= 3
    ? relatedMovies
    : [...relatedMovies, ...movies.filter((m) => m.id !== id).slice(0, 4 - relatedMovies.length)];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    if (!movie) return;

    const ldJson = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.yemaocinema.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: `${movie.category}片`,
              item: `https://www.yemaocinema.com/category/${movie.categorySlug}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: movie.title,
              item: `https://www.yemaocinema.com/article/${movie.id}`,
            },
          ],
        },
        {
          '@type': 'Article',
          name: movie.title,
          headline: movie.title,
          description: movie.description,
          url: `https://www.yemaocinema.com/article/${movie.id}`,
          datePublished: `${movie.year}-01-01`,
          author: {
            '@type': 'Person',
            name: movie.director,
          },
          image: movie.poster,
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ldJson);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [movie]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-800/50 mb-4">
              <i className="ri-error-warning-line text-foreground-600 text-2xl"></i>
            </div>
            <h2 className="text-foreground-200 text-lg font-medium mb-2">影片未找到</h2>
            <p className="text-foreground-500 text-sm mb-6">抱歉，该影片可能已被下架或链接不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16 min-h-screen">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 border-b border-background-700/30">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
              <span className="text-foreground-700">/</span>
              <Link to={`/category/${movie.categorySlug}`} className="hover:text-primary-400 transition-colors cursor-pointer">{movie.category}片</Link>
              <span className="text-foreground-700">/</span>
              <span className="text-foreground-300">{movie.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster */}
              <div className="lg:w-80 flex-shrink-0">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    title={`${movie.title} - 夜猫影院高清在线观看`}
                    className="w-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 p-4 rounded-lg bg-background-800/40 border border-background-700/30">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-500">评分</span>
                      <span className="text-primary-400 font-bold flex items-center gap-1">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill"></i>
                        </div>
                        {movie.rating}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">年份</span>
                      <span className="text-foreground-300">{movie.year}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">时长</span>
                      <span className="text-foreground-300">{movie.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">地区</span>
                      <span className="text-foreground-300">{movie.region}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">导演</span>
                      <span className="text-foreground-300">{movie.director}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">播放量</span>
                      <span className="text-foreground-300">{movie.views > 10000 ? `${(movie.views / 10000).toFixed(1)}万` : movie.views}次</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-4">
                  {movie.title}
                </h1>

                {/* Tags */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium border border-primary-500/20 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Actors */}
                <div className="mb-6">
                  <h3 className="text-foreground-400 text-xs font-medium mb-2">主演</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {movie.actors.map((actor) => (
                      <span
                        key={actor}
                        className="px-3 py-1 rounded-md bg-background-800/50 text-foreground-300 text-xs border border-background-700/30"
                      >
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="mb-8">
                  <h3 className="text-foreground-400 text-xs font-medium mb-2">剧情简介</h3>
                  <p className="text-foreground-400 text-sm leading-relaxed">{movie.description}</p>
                </div>

                {/* CTA */}
                <Link
                  to={`/category/${movie.categorySlug}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-play-fill"></i>
                  </div>
                  观看更多{movie.category}片
                </Link>
              </div>
            </div>

            {/* Recommended */}
            {recommendedMovies.length > 0 && (
              <div className="mt-14">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-heading text-xl font-bold text-foreground-50 flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-thumb-up-line text-primary-500"></i>
                    </div>
                    为你推荐
                  </h2>
                  <Link
                    to={`/category/${movie.categorySlug}`}
                    className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多{movie.category}片 <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {recommendedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}